import { ServiceResponse } from '@nx-smartmonkey/shared/hooks';
import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';

import { planService } from '../../../../../application/PlanService';
import useErrorMiddleware from '../../../../../hooks/errorMiddleware/useErrorMiddleware';
import { useRetrieveActiveProject } from '../../../../../hooks/project/useRetrieveActiveProject';
import { useShowSnackbarMessage } from '../../../../../hooks/snackbarMessage/useShowSnackbarMessage';
import { useCustomParams } from '../../../../../hooks/useCustomParams';
import { PlanOptimizationData } from '../../../../../repositories/plan/PlanRepository';
import { PlanDetailsActionType } from '../../context/actions';
import { usePlanDetailsContext } from '../../context/context';
import { OptimizationData } from '../../context/reducer';

type UseRetrieveActivePlanOptimizationResponse = ServiceResponse<Function, OptimizationData>;

// Hooks act as 'Adapter' layer.
export const useRetrieveActivePlanOptimization = (): UseRetrieveActivePlanOptimizationResponse => {
  const { data: project } = useRetrieveActiveProject();
  const { showSnackbarMessage } = useShowSnackbarMessage();
  const { planDetailsState, planDetailsDispatch } = usePlanDetailsContext();
  const { sessionExpiredLogout } = useErrorMiddleware();

  const { value: planId } = useCustomParams({ param: `planId` });

  const { isLoading, isError, isSuccess, refetch } = useQuery(
    [`planner_active_plan_optimization`, planId],
    () => planService.getPlanOptimization({ planId: planId! }),
    {
      staleTime: Infinity,
      enabled: planId !== undefined && project !== undefined,
      retry: false,
      refetchOnWindowFocus: false,
      onSuccess: (optimizationData: PlanOptimizationData) => {
        planDetailsDispatch({
          type: PlanDetailsActionType.SET_ACTIVE_PLAN_OPTIMIZATION_DATA,
          payload: {
            status: optimizationData.status,
            startedAt: optimizationData.status === `running` ? optimizationData.createdAt : undefined,
          },
        });
      },
      onError: (err: any) => {
        console.info(`Planner Retrieve Active Plan Optimization Error: `, err?.response);

        if (err?.response?.status === 401) {
          sessionExpiredLogout();
          return;
        }

        showSnackbarMessage({
          messageIntlId: `infoerror.${err?.response?.data?.messageId ?? `network_error`}`,
        });
      },
    }
  );

  useEffect(() => {
    if (planId && planDetailsState.plan?.id !== planId) {
      refetch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planId]);

  return {
    error: isError,
    isLoading,
    success: isSuccess,
    run: refetch,
    data: planDetailsState.optimization,
  };
};
